import React, { useState, useEffect } from 'react';
import { Search, Eye, RefreshCw, Trash2, ChevronLeft, ChevronRight, Download, UserPlus, Globe, Calendar, Mail, Phone, MessageSquare, ExternalLink, MapPin, GraduationCap, Target, Languages, DollarSign, Package, Heart, Settings, X } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface WebsiteFormSubmission {
  id: string;
  applicant_type: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  city: string | null;
  district: string | null;
  postal_code: string | null;
  address: string | null;
  office: string | null;
  academic_term: string | null;
  language_preference: string | null;
  graduated_school: string | null;
  graduation_year: string | null;
  gpa: string | null;
  current_education_status: string | null;
  education_level: string | null;
  interested_field: string | null;
  target_university: string | null;
  italian_level: string | null;
  english_level: string | null;
  certificates: string[] | null;
  certificate_score: string | null;
  budget: string[] | null;
  budget_other: string | null;
  scholarship_interest: string | null;
  services: string[] | null;
  service_other: string | null;
  motivation: string | null;
  additional_info: string | null;
  kvkk_accepted: boolean | null;
  marketing_consent: boolean | null;
  interested_service: string | null;
  message: string | null;
  source_url: string | null;
  referring_domain: string | null;
  ip_address: string | null;
  user_agent: string | null;
  status: 'new' | 'viewed' | 'called' | 'email_sent' | 'message_sent' | 'converted';
  converted_lead_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  is_synced_to_inbox?: boolean;
}

const WebsiteData: React.FC = () => {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState<WebsiteFormSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSubmission, setSelectedSubmission] = useState<WebsiteFormSubmission | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('website_form_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSubmissionStatus = async (id: string, status: 'new' | 'viewed' | 'called' | 'email_sent' | 'message_sent' | 'converted') => {
    // A converted status must represent an actual lead, not just a label change.
    if (status === 'converted') {
      const submission = submissions.find(item => item.id === id);
      if (submission && !submission.converted_lead_id) {
        setSelectedSubmission(submission);
        setShowConvertModal(true);
      }
      return;
    }

    try {
      const { error } = await supabase
        .from('website_form_submissions')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      await fetchSubmissions();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Durum güncellenirken hata oluştu.');
    }
  };

  const deleteSubmission = async (id: string) => {
    try {
      const { error } = await supabase
        .from('website_form_submissions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchSubmissions();
      setShowDeleteConfirm(false);
      setSelectedSubmission(null);
    } catch (error) {
      console.error('Error deleting submission:', error);
      alert('Form silinirken hata oluştu.');
    }
  };

  const convertToLead = async (submission: WebsiteFormSubmission) => {
    if (isConverting || submission.converted_lead_id) return;
    if (!user?.id) {
      alert('Oturum bilgisi bulunamadı. Lütfen tekrar giriş yapın.');
      return;
    }

    setIsConverting(true);
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('branch_id')
        .eq('id', user?.id)
        .single();

      if (profileError) throw profileError;
      if (!profile?.branch_id) {
        alert('Branch bilgisi bulunamadı. Lütfen sistem yöneticisi ile iletişime geçin.');
        return;
      }

      let notesText = `Website formundan otomatik dönüştürüldü.\n\n`;

      if (submission.motivation) {
        notesText += `Motivasyon: ${submission.motivation}\n\n`;
      }
      if (submission.interested_field) {
        notesText += `İlgilenilen Alan: ${submission.interested_field}\n`;
      }
      if (submission.target_university) {
        notesText += `Hedef Üniversite: ${submission.target_university}\n`;
      }
      if (submission.graduated_school) {
        notesText += `Mezun Olunan Okul: ${submission.graduated_school}\n`;
      }
      if (submission.gpa) {
        notesText += `Diploma Notu: ${submission.gpa}\n`;
      }
      if (submission.additional_info) {
        notesText += `\nEk Bilgi: ${submission.additional_info}`;
      }

      const interestedServices = [];
      if (submission.services && submission.services.length > 0) {
        submission.services.forEach(service => {
          interestedServices.push(getServicesText(service));
        });
      }
      if (submission.interested_service) {
        interestedServices.push(getServiceText(submission.interested_service));
      }

      const leadData = {
        first_name: submission.first_name,
        last_name: submission.last_name,
        emails: [submission.email],
        phone: submission.phone,
        source: 'website',
        status: 'lead',
        priority: 'medium',
        interested_services: interestedServices.length > 0 ? interestedServices : ['Üniversite Başvurusu'],
        notes: notesText,
        created_by: user?.id,
        assigned_consultants: [user.id],
        branch_id: profile.branch_id,
        contact_role: submission.applicant_type === 'parent' ? 'guardian' : 'student',
        interested_countries: ['İtalya'],
        interested_programs: submission.interested_field ? [submission.interested_field] : [],
        meeting_type: 'phone',
        budget: submission.budget_other || null,
        english_level: submission.english_level ? getLanguageLevelText(submission.english_level) : null,
        education_level: submission.education_level ? getEducationLevelText(submission.education_level) : null,
        created_at: new Date().toISOString()
      };

      const { data: leadCreated, error: leadError } = await supabase
        .from('leads')
        .insert(leadData)
        .select()
        .single();

      if (leadError) throw leadError;

      const { error: updateError } = await supabase
        .from('website_form_submissions')
        .update({
          status: 'converted',
          converted_lead_id: leadCreated.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', submission.id)
        .select('id')
        .single();

      if (updateError) throw updateError;

      window.dispatchEvent(new CustomEvent('newLeadAdded'));

      // Send welcome SMS if phone number exists
      if (leadCreated?.id && submission.phone) {
        try {
          const { error: smsError } = await supabase.functions.invoke('send-welcome-sms', {
            body: {
              lead_id: leadCreated.id,
              first_name: submission.first_name,
              last_name: submission.last_name,
              phone: submission.phone
            }
          });

          if (smsError) {
            console.error('SMS gönderme hatası:', smsError);
          }
        } catch (smsErr) {
          console.error('SMS gönderme hatası:', smsErr);
        }
      }

      alert('Form başarıyla lead\'e dönüştürüldü!');
      setShowConvertModal(false);
      setSelectedSubmission(null);
      await fetchSubmissions();
    } catch (error) {
      console.error('Error converting to lead:', error);
      alert('Lead\'e dönüştürme hatası oluştu. Detay: ' + (error as any).message);
    } finally {
      setIsConverting(false);
    }
  };

  // Check for unsynced approved contacts and sync them
  useEffect(() => {
    if (submissions.length > 0) {
      autoSyncApprovedContacts();
    }
  }, [submissions]);

  const autoSyncApprovedContacts = async () => {
    // Filter contacts that are approved BUT not synced
    const unsyncedContacts = submissions.filter(s => s.marketing_consent === true && !s.is_synced_to_inbox);

    if (unsyncedContacts.length === 0) return;

    console.log(`Otomatik senkronizasyon: ${unsyncedContacts.length} kişi Inbox'a gönderilecek.`);

    for (const submission of unsyncedContacts) {
      await syncToInbox(submission, true); // true = silent mode (no alert)
    }
  };

  const syncToInbox = async (submission: WebsiteFormSubmission, silent: boolean = false) => {
    const token = localStorage.getItem('inbox_token');
    if (!token) {
      if (!silent) alert("Inbox entegrasyonu için token bulunamadı. Lütfen önce Newsletter sayfasından Inbox'a giriş yapın.");
      return;
    }

    try {
      // 1. Fetch Lists to find 'Website Contacts'
      let listId = '';
      const listResp = await fetch('https://useapi.useinbox.com/inbox/v1/contactlists', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (listResp.ok) {
        const listData = await listResp.json();
        const lists = listData.resultObject?.items || listData.resultObject || [];
        const targetList = lists.find((l: any) => l.listName === 'Website Formları' || l.Name === 'Website Formları');

        if (targetList) {
          listId = targetList.id;
        } else {
          // 1.1 If not found, we need a group to create it. Fetch groups.
          const groupResp = await fetch('https://useapi.useinbox.com/inbox/v1/groups', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (groupResp.ok) {
            const groupData = await groupResp.json();
            const groups = groupData.resultObject?.items || groupData.resultObject || [];
            if (groups.length > 0) {
              // Create list
              const createResp = await fetch('https://useapi.useinbox.com/inbox/v1/contactlists', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  listName: 'Website Formları',
                  groupId: groups[0].id,
                  legislation: 0
                })
              });
              if (createResp.ok) {
                const createData = await createResp.json();
                listId = createData.resultObject.id;
              }
            }
          }
        }
      }

      if (!listId) {
        if (!silent) alert('Kişi listesi senkronizasyonu başarısız: Hedef liste hazırlanamadı.');
        return;
      }

      // 2. Add Contact to List
      const customFields = [];
      if (submission.first_name) customFields.push({ customFieldId: 'Name', value: submission.first_name });
      if (submission.last_name) customFields.push({ customFieldId: 'Surname', value: submission.last_name });

      const addResp = await fetch(`https://useapi.useinbox.com/inbox/v1/contactlists/${listId}/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: submission.email,
          customFields: customFields
        })
      });

      const addData = await addResp.json();

      if (addData.resultStatus) {
        if (!silent) alert(`"${submission.first_name} ${submission.last_name}" Inbox "Website Formları" listesine eklendi.`);

        // 3. Mark as synced in DB
        await supabase
          .from('website_form_submissions')
          .update({ is_synced_to_inbox: true })
          .eq('id', submission.id);

        // Update local state
        setSubmissions(prev => prev.map(s =>
          s.id === submission.id ? { ...s, is_synced_to_inbox: true } : s
        ));

      } else {
        console.warn('Inbox Add Result:', addData);
      }

    } catch (err) {
      console.error('Inbox Sync Error:', err);
      if (!silent) alert('Inbox senkronizasyonu sırasında hata oluştu.');
    }
  };

  const toggleMarketingConsent = async (submission: WebsiteFormSubmission) => {
    try {
      const newConsent = !submission.marketing_consent;

      const { error } = await supabase
        .from('website_form_submissions')
        .update({
          marketing_consent: newConsent,
          updated_at: new Date().toISOString()
        })
        .eq('id', submission.id);

      if (error) throw error;

      // Update local state
      setSubmissions(prev => prev.map(s =>
        s.id === submission.id ? { ...s, marketing_consent: newConsent } : s
      ));

      if (selectedSubmission && selectedSubmission.id === submission.id) {
        setSelectedSubmission({ ...selectedSubmission, marketing_consent: newConsent });
      }

      // Sync if approved
      if (newConsent) {
        await syncToInbox(submission);
      }

    } catch (error) {
      console.error('Error updating consent:', error);
      alert('İzin durumu güncellenirken hata oluştu.');
    }
  };

  const getServiceText = (service: string | null) => {
    if (!service) return '-';
    const services: Record<string, string> = {
      'language_courses': 'Dil Kursları',
      'university_application': 'Üniversite Başvurusu',
      'visa_support': 'Vize Desteği',
      'accommodation': 'Konaklama',
      'health_insurance': 'Sağlık Sigortası',
      'exam_prep': 'Sınav Hazırlık'
    };
    return services[service] || service;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 border border-blue-200 dark:border-blue-800';
      case 'viewed':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800';
      case 'called':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400 border border-purple-200 dark:border-purple-800';
      case 'email_sent':
        return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-400 border border-cyan-200 dark:border-cyan-800';
      case 'message_sent':
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900/20 dark:text-pink-400 border border-pink-200 dark:border-pink-800';
      case 'converted':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 border border-green-200 dark:border-green-800';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'new': return 'Yeni';
      case 'viewed': return 'Görüldü';
      case 'called': return 'Arandı';
      case 'email_sent': return 'Mail Atıldı';
      case 'message_sent': return 'Mesaj İletildi';
      case 'converted': return 'Dönüştürüldü';
      default: return status;
    }
  };

  const getEducationLevelText = (level: string | null) => {
    if (!level) return '-';
    const levels: Record<string, string> = {
      'bachelor': 'Lisans',
      'master': 'Yüksek Lisans',
      'doctorate': 'Doktora',
      'high_school': 'Lise',
      'associate': 'Ön Lisans'
    };
    return levels[level] || level;
  };

  const getLanguageLevelText = (level: string | null) => {
    if (!level) return '-';
    const levels: Record<string, string> = {
      'none': 'Yok',
      'beginner': 'Başlangıç',
      'elementary': 'Temel',
      'intermediate': 'Orta Seviye',
      'upper_intermediate': 'Orta-İleri Seviye',
      'advanced': 'İleri Seviye',
      'proficient': 'Akıcı',
      'native': 'Anadil'
    };
    return levels[level] || level;
  };

  const getCertificateText = (cert: string) => {
    const certificates: Record<string, string> = {
      'ielts': 'IELTS',
      'toefl': 'TOEFL',
      'celi': 'CELI',
      'cils': 'CILS',
      'plida': 'PLIDA',
      'cambridge': 'Cambridge',
      'delf': 'DELF',
      'dele': 'DELE',
      'testdaf': 'TestDaF',
      'goethe': 'Goethe',
      'sat': 'SAT',
      'act': 'ACT',
      'gre': 'GRE',
      'gmat': 'GMAT'
    };
    return certificates[cert] || cert;
  };

  const getBudgetText = (budget: string) => {
    const budgets: Record<string, string> = {
      '0-5000': '0-5.000 EUR',
      '5000-10000': '5.000-10.000 EUR',
      '10000-15000': '10.000-15.000 EUR',
      '15000-20000': '15.000-20.000 EUR',
      '20000+': '20.000+ EUR',
      'flexible': 'Esnek'
    };
    return budgets[budget] || budget;
  };

  const getScholarshipInterestText = (interest: string | null) => {
    if (!interest) return '-';
    const interests: Record<string, string> = {
      'yes': 'Evet',
      'no': 'Hayır',
      'maybe': 'Belki'
    };
    return interests[interest] || interest;
  };

  const getServicesText = (service: string) => {
    const services: Record<string, string> = {
      'language_courses': 'Dil Kursları',
      'university_application': 'Üniversite Başvurusu',
      'visa_support': 'Vize Desteği',
      'accommodation': 'Konaklama',
      'health_insurance': 'Sağlık Sigortası',
      'exam_prep': 'Sınav Hazırlık',
      'career_counseling': 'Kariyer Danışmanlığı',
      'scholarship_search': 'Burs Arama',
      'document_translation': 'Belge Çevirisi',
      'airport_pickup': 'Havalimanı Karşılama'
    };
    return services[service] || service;
  };

  const getOfficeText = (office: string | null) => {
    if (!office) return '-';
    const offices: Record<string, string> = {
      'istanbul_europe': 'İstanbul Avrupa',
      'istanbul_asia': 'İstanbul Anadolu',
      'izmir': 'İzmir',
      'bursa': 'Bursa',
      'ankara': 'Ankara',
      'fethiye': 'Fethiye'
    };
    return offices[office] || office;
  };

  const getAcademicTermText = (term: string | null) => {
    if (!term) return '-';
    const terms: Record<string, string> = {
      'fall': 'Güz Dönemi',
      'spring': 'Bahar Dönemi',
      'summer': 'Yaz Dönemi',
      '2024-2025': '2024-2025',
      '2025-2026': '2025-2026'
    };
    return terms[term] || term;
  };

  const getLanguagePreferenceText = (lang: string | null) => {
    if (!lang) return '-';
    const languages: Record<string, string> = {
      'turkish': 'Türkçe',
      'english': 'İngilizce',
      'italian': 'İtalyanca'
    };
    return languages[lang] || lang;
  };

  const filteredSubmissions = submissions.filter(submission => {
    const matchesSearch =
      searchTerm === '' ||
      submission.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.phone?.includes(searchTerm);

    const matchesStatus = statusFilter === 'all' || submission.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: submissions.length,
    new: submissions.filter(s => s.status === 'new').length,
    viewed: submissions.filter(s => s.status === 'viewed').length,
    called: submissions.filter(s => s.status === 'called').length,
    email_sent: submissions.filter(s => s.status === 'email_sent').length,
    message_sent: submissions.filter(s => s.status === 'message_sent').length,
    converted: submissions.filter(s => s.status === 'converted').length,
  };

  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSubmissions = filteredSubmissions.slice(startIndex, endIndex);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const handleExportToExcel = () => {
    const dataToExport = filteredSubmissions.map(submission => ({
      'Başvuran Tipi': submission.applicant_type === 'student' ? 'Öğrenci' : submission.applicant_type === 'parent' ? 'Veli' : '',
      'Ad': submission.first_name,
      'Soyad': submission.last_name,
      'Email': submission.email,
      'Telefon': submission.phone || '',
      'Şehir': submission.city || '',
      'İlçe': submission.district || '',
      'Posta Kodu': submission.postal_code || '',
      'Adres': submission.address || '',
      'Ofis': submission.office || '',
      'Akademik Dönem': submission.academic_term || '',
      'Dil Tercihi': submission.language_preference || '',
      'Mezun Olunan Okul': submission.graduated_school || '',
      'Mezuniyet Yılı': submission.graduation_year || '',
      'Diploma Notu': submission.gpa || '',
      'Mevcut Eğitim Durumu': submission.current_education_status || '',
      'Eğitim Seviyesi': submission.education_level || '',
      'İlgilenilen Alan': submission.interested_field || '',
      'Hedef Üniversite': submission.target_university || '',
      'İtalyanca Seviyesi': submission.italian_level || '',
      'İngilizce Seviyesi': submission.english_level || '',
      'Sertifikalar': submission.certificates ? submission.certificates.join(', ') : '',
      'Sertifika Puanı': submission.certificate_score || '',
      'Bütçe': submission.budget ? submission.budget.join(', ') : '',
      'Bütçe Diğer': submission.budget_other || '',
      'Burs İlgisi': submission.scholarship_interest || '',
      'Hizmetler': submission.services ? submission.services.join(', ') : '',
      'Hizmet Diğer': submission.service_other || '',
      'Motivasyon': submission.motivation || '',
      'Ek Bilgi': submission.additional_info || '',
      'KVKK Kabul': submission.kvkk_accepted ? 'Evet' : 'Hayır',
      'İletişim İzni': submission.marketing_consent ? 'Evet' : 'Hayır',
      'İlgilenilen Hizmet (Eski)': getServiceText(submission.interested_service),
      'Mesaj (Eski)': submission.message || '',
      'Kaynak Domain': submission.referring_domain || '',
      'Kaynak URL': submission.source_url || '',
      'Durum': getStatusText(submission.status),
      'Gönderim Tarihi': format(new Date(submission.created_at), 'dd/MM/yyyy HH:mm', { locale: tr }),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Website Formları');
    const fileName = `website_form_submissions_${format(new Date(), 'dd-MM-yyyy_HH-mm')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Website Dataları</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Toplam {filteredSubmissions.length} form gönderimi • Sayfa {currentPage} / {totalPages}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchSubmissions}
            disabled={loading}
            className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Yenile</span>
          </button>
          <button
            onClick={handleExportToExcel}
            disabled={filteredSubmissions.length === 0}
            className="flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            <span>Dışa Aktar</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
          <div className="flex flex-col">
            <Globe className="w-6 h-6 text-blue-500 mb-2" />
            <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Toplam</p>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
          <div className="flex flex-col">
            <Mail className="w-6 h-6 text-blue-500 mb-2" />
            <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.new}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Yeni</p>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
          <div className="flex flex-col">
            <Eye className="w-6 h-6 text-yellow-500 mb-2" />
            <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.viewed}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Görüldü</p>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
          <div className="flex flex-col">
            <Phone className="w-6 h-6 text-purple-500 mb-2" />
            <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.called}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Arandı</p>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
          <div className="flex flex-col">
            <Mail className="w-6 h-6 text-cyan-500 mb-2" />
            <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.email_sent}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Mail Atıldı</p>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
          <div className="flex flex-col">
            <MessageSquare className="w-6 h-6 text-pink-500 mb-2" />
            <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.message_sent}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Mesaj İletildi</p>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
          <div className="flex flex-col">
            <UserPlus className="w-6 h-6 text-green-500 mb-2" />
            <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.converted}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Dönüştürüldü</p>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="İsim, email veya telefon ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tüm Durumlar</option>
            <option value="new">Yeni</option>
            <option value="viewed">Görüldü</option>
            <option value="called">Arandı</option>
            <option value="email_sent">Mail Atıldı</option>
            <option value="message_sent">Mesaj İletildi</option>
            <option value="converted">Dönüştürüldü</option>
          </select>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600 dark:text-gray-400">Formlar yükleniyor...</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
                  <tr>
                    <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Kişi</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İletişim</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İlgilenilen Bölüm</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İstenilen Üniversite</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İlgilenilen Hizmetler</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Durum</th>
                    <th className="text-left py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">Tarih</th>
                    <th className="text-right py-3 px-6 text-sm font-medium text-gray-900 dark:text-white">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedSubmissions.map((submission) => (
                    <tr key={submission.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-semibold">
                              {submission.first_name[0]}{submission.last_name[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {submission.first_name} {submission.last_name}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                            <Mail className="w-4 h-4" />
                            <span className="truncate max-w-48">{submission.email}</span>
                          </div>
                          {submission.phone && (
                            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                              <Phone className="w-4 h-4" />
                              <span>{submission.phone}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {submission.interested_field || '-'}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {submission.target_university || '-'}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {submission.services && submission.services.length > 0 ? (
                            submission.services.slice(0, 2).map((service, idx) => (
                              <span key={idx} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs whitespace-nowrap">
                                {getServicesText(service)}
                              </span>
                            ))
                          ) : submission.interested_service ? (
                            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs whitespace-nowrap">
                              {getServiceText(submission.interested_service)}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-500">-</span>
                          )}
                          {submission.services && submission.services.length > 2 && (
                            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded text-xs">
                              +{submission.services.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <select
                          value={submission.status}
                          onChange={(e) => updateSubmissionStatus(submission.id, e.target.value as any)}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer border-0 focus:ring-2 focus:ring-blue-500 ${getStatusColor(submission.status)}`}
                        >
                          <option value="new">Yeni</option>
                          <option value="viewed">Görüldü</option>
                          <option value="called">Arandı</option>
                          <option value="email_sent">Mail Atıldı</option>
                          <option value="message_sent">Mesaj İletildi</option>
                          <option value="converted">Dönüştürüldü</option>
                        </select>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                          <Calendar className="w-4 h-4" />
                          <span>{format(new Date(submission.created_at), 'dd MMM yyyy HH:mm', { locale: tr })}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => {
                              setSelectedSubmission(submission);
                              setShowDetailModal(true);
                              if (submission.status === 'new') {
                                updateSubmissionStatus(submission.id, 'viewed');
                              }
                            }}
                            className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            title="Detayları Görüntüle"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {!submission.converted_lead_id && (
                            <button
                              onClick={() => {
                                setSelectedSubmission(submission);
                                setShowConvertModal(true);
                              }}
                              className="p-2 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                              title="Lead'e Dönüştür"
                            >
                              <UserPlus className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setSelectedSubmission(submission);
                              setShowDeleteConfirm(true);
                            }}
                            className="p-2 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="Sil"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {!loading && filteredSubmissions.length > 0 && totalPages > 1 && (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Gösterilen: {startIndex + 1}-{Math.min(endIndex, filteredSubmissions.length)} / {filteredSubmissions.length}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg font-medium">
                {currentPage}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Sayfa {currentPage} / {totalPages}
            </div>
          </div>
        </div>
      )}

      {!loading && filteredSubmissions.length === 0 && (
        <div className="text-center py-12">
          <Globe className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {searchTerm ? 'Form bulunamadı' : 'Henüz form gönderimi yok'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {searchTerm
              ? 'Arama kriterlerinizi değiştirmeyi deneyin'
              : 'Website formundan gönderim yapıldığında burada görünecek'
            }
          </p>
        </div>
      )}

      {showDetailModal && selectedSubmission && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="sticky top-0 z-10 bg-emerald-600 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-white">
                  {selectedSubmission.first_name} {selectedSubmission.last_name}
                </h3>
                <p className="text-emerald-100 text-sm">
                  {selectedSubmission.applicant_type === 'student' ? 'Öğrenci' : 'Veli'} Başvurusu
                </p>
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedSubmission(null);
                }}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-6 text-sm text-gray-700 dark:text-gray-300">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>{selectedSubmission.email}</span>
                </div>
                {selectedSubmission.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <span>{selectedSubmission.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 ml-auto">
                  <Calendar className="w-4 h-4" />
                  <span>{format(new Date(selectedSubmission.created_at), 'dd MMM yyyy', { locale: tr })}</span>
                </div>
              </div>
            </div>

            <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {(selectedSubmission.city || selectedSubmission.district || selectedSubmission.address) && (
                  <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <h4 className="font-semibold text-gray-900 dark:text-white">Adres Bilgileri</h4>
                    </div>
                    <div className="space-y-2">
                      {selectedSubmission.city && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">ŞEHİR</label>
                          <p className="text-gray-900 dark:text-white">{selectedSubmission.city}</p>
                        </div>
                      )}
                      {selectedSubmission.district && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">İLÇE</label>
                          <p className="text-gray-900 dark:text-white">{selectedSubmission.district}</p>
                        </div>
                      )}
                      {selectedSubmission.postal_code && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">POSTA KODU</label>
                          <p className="text-gray-900 dark:text-white">{selectedSubmission.postal_code}</p>
                        </div>
                      )}
                      {selectedSubmission.address && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">ADRES</label>
                          <p className="text-gray-900 dark:text-white">{selectedSubmission.address}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {(selectedSubmission.graduated_school || selectedSubmission.gpa || selectedSubmission.education_level) && (
                  <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-3">
                      <GraduationCap className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      <h4 className="font-semibold text-gray-900 dark:text-white">Eğitim Bilgileri</h4>
                    </div>
                    <div className="space-y-2">
                      {selectedSubmission.graduated_school && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">MEZUN OLUNAN OKUL</label>
                          <p className="text-gray-900 dark:text-white">{selectedSubmission.graduated_school}</p>
                        </div>
                      )}
                      {selectedSubmission.graduation_year && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">MEZUNİYET YILI</label>
                          <p className="text-gray-900 dark:text-white">{selectedSubmission.graduation_year}</p>
                        </div>
                      )}
                      {selectedSubmission.gpa && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">DIPLOMA NOTU</label>
                          <p className="text-gray-900 dark:text-white">{selectedSubmission.gpa}</p>
                        </div>
                      )}
                      {selectedSubmission.current_education_status && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">MEVCUT EĞİTİM DURUMU</label>
                          <p className="text-gray-900 dark:text-white">{getEducationLevelText(selectedSubmission.current_education_status)}</p>
                        </div>
                      )}
                      {selectedSubmission.education_level && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">EĞİTİM SEVİYESİ</label>
                          <p className="text-gray-900 dark:text-white">{getEducationLevelText(selectedSubmission.education_level)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {(selectedSubmission.interested_field || selectedSubmission.target_university) && (
                  <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-3">
                      <Target className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                      <h4 className="font-semibold text-gray-900 dark:text-white">İlgi Alanları</h4>
                    </div>
                    <div className="space-y-2">
                      {selectedSubmission.interested_field && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">İLGİLENİLEN ALAN</label>
                          <p className="text-gray-900 dark:text-white">{selectedSubmission.interested_field}</p>
                        </div>
                      )}
                      {selectedSubmission.target_university && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">HEDEF ÜNİVERSİTE</label>
                          <p className="text-gray-900 dark:text-white">{selectedSubmission.target_university}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {(selectedSubmission.italian_level || selectedSubmission.english_level || selectedSubmission.certificates) && (
                  <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-3">
                      <Languages className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                      <h4 className="font-semibold text-gray-900 dark:text-white">Dil Bilgileri</h4>
                    </div>
                    <div className="space-y-2">
                      {selectedSubmission.italian_level && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">İTALYANCA SEVİYESİ</label>
                          <p className="text-gray-900 dark:text-white">{getLanguageLevelText(selectedSubmission.italian_level)}</p>
                        </div>
                      )}
                      {selectedSubmission.english_level && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">İNGİLİZCE SEVİYESİ</label>
                          <p className="text-gray-900 dark:text-white">{getLanguageLevelText(selectedSubmission.english_level)}</p>
                        </div>
                      )}
                      {selectedSubmission.certificates && selectedSubmission.certificates.length > 0 && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">SERTİFİKALAR</label>
                          <p className="text-gray-900 dark:text-white">{selectedSubmission.certificates.map(cert => getCertificateText(cert)).join(', ')}</p>
                        </div>
                      )}
                      {selectedSubmission.certificate_score && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">SERTİFİKA PUANI</label>
                          <p className="text-gray-900 dark:text-white">{selectedSubmission.certificate_score}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {(selectedSubmission.budget || selectedSubmission.scholarship_interest) && (
                  <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-3">
                      <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <h4 className="font-semibold text-gray-900 dark:text-white">Bütçe ve Burs</h4>
                    </div>
                    <div className="space-y-2">
                      {selectedSubmission.budget && selectedSubmission.budget.length > 0 && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">BÜTÇE</label>
                          <p className="text-gray-900 dark:text-white">{selectedSubmission.budget.map(b => getBudgetText(b)).join(', ')}</p>
                        </div>
                      )}
                      {selectedSubmission.budget_other && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">DİĞER BÜTÇE</label>
                          <p className="text-gray-900 dark:text-white">{selectedSubmission.budget_other}</p>
                        </div>
                      )}
                      {selectedSubmission.scholarship_interest && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">BURS İLGİSİ</label>
                          <p className="text-gray-900 dark:text-white">{getScholarshipInterestText(selectedSubmission.scholarship_interest)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedSubmission.services && selectedSubmission.services.length > 0 && (
                  <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-3">
                      <Package className="w-4 h-4 text-pink-600 dark:text-pink-400" />
                      <h4 className="font-semibold text-gray-900 dark:text-white">İlgilenilen Hizmetler</h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedSubmission.services.map((service, idx) => (
                        <span key={idx} className="px-2 py-1 bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 rounded text-xs">
                          {getServicesText(service)}
                        </span>
                      ))}
                    </div>
                    {selectedSubmission.service_other && (
                      <p className="text-gray-700 dark:text-gray-300 mt-2 text-sm">Diğer: {selectedSubmission.service_other}</p>
                    )}
                  </div>
                )}

                {(selectedSubmission.office || selectedSubmission.academic_term || selectedSubmission.language_preference) && (
                  <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 mb-3">
                      <Settings className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      <h4 className="font-semibold text-gray-900 dark:text-white">Tercihler</h4>
                    </div>
                    <div className="space-y-2">
                      {selectedSubmission.office && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">OFİS</label>
                          <p className="text-gray-900 dark:text-white">{getOfficeText(selectedSubmission.office)}</p>
                        </div>
                      )}
                      {selectedSubmission.academic_term && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">AKADEMİK DÖNEM</label>
                          <p className="text-gray-900 dark:text-white">{getAcademicTermText(selectedSubmission.academic_term)}</p>
                        </div>
                      )}
                      {selectedSubmission.language_preference && (
                        <div>
                          <label className="text-xs text-gray-500 dark:text-gray-400">DİL TERCİHİ</label>
                          <p className="text-gray-900 dark:text-white">{getLanguagePreferenceText(selectedSubmission.language_preference)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {selectedSubmission.motivation && (
                <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Heart className="w-4 h-4 text-red-600 dark:text-red-400" />
                    <h4 className="font-semibold text-gray-900 dark:text-white">Motivasyon</h4>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap text-sm">{selectedSubmission.motivation}</p>
                </div>
              )}

              {selectedSubmission.additional_info && (
                <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    <h4 className="font-semibold text-gray-900 dark:text-white">Ek Bilgi</h4>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap text-sm">{selectedSubmission.additional_info}</p>
                </div>
              )}

              {(selectedSubmission.referring_domain || selectedSubmission.source_url) && (
                <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <ExternalLink className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                    <h4 className="font-semibold text-gray-900 dark:text-white">Kaynak Bilgisi</h4>
                  </div>
                  <div className="space-y-2">
                    {selectedSubmission.referring_domain && (
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400">KAYNAK WEBSİTE</label>
                        <div className="flex items-center gap-2 mt-1">
                          <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          <p className="text-gray-900 dark:text-white font-medium">{selectedSubmission.referring_domain}</p>
                        </div>
                      </div>
                    )}
                    {selectedSubmission.source_url && selectedSubmission.source_url !== 'unknown' && (
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400">KAYNAK SAYFA URL</label>
                        <a
                          href={selectedSubmission.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline break-all text-sm block mt-1"
                        >
                          {selectedSubmission.source_url}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">İletişim İzni</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Kampanya ve bilgilendirme onayı</p>
                  </div>
                  <button
                    onClick={() => toggleMarketingConsent(selectedSubmission)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors cursor-pointer hover:opacity-80 ${selectedSubmission.marketing_consent ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'}`}
                    title="İzni Durumunu Değiştir"
                  >
                    {selectedSubmission.marketing_consent ? 'Onaylandı' : 'Onaylanmadı'}
                  </button>
                </div>
              </div>
            </div >

            <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 px-6 py-3 flex items-center justify-end">
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedSubmission(null);
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
              >
                Kapat
              </button>
            </div>
          </div >
        </div >
      )}

      {
        showConvertModal && selectedSubmission && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-md mx-4">
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                    <UserPlus className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Lead'e Dönüştür</h3>
                    <p className="text-gray-600 dark:text-gray-400">Bu form lead'e dönüştürülecek</p>
                  </div>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-6">
                  <strong>{selectedSubmission.first_name} {selectedSubmission.last_name}</strong> adlı kişinin form bilgileri lead olarak kaydedilecek. Devam etmek istiyor musunuz?
                </p>
                <div className="flex items-center justify-end space-x-3">
                  <button
                    disabled={isConverting}
                    onClick={() => {
                      setShowConvertModal(false);
                      setSelectedSubmission(null);
                    }}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    disabled={isConverting}
                    onClick={() => convertToLead(selectedSubmission)}
                    className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span>{isConverting ? 'Dönüştürülüyor...' : 'Dönüştür'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {
        showDeleteConfirm && selectedSubmission && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-md mx-4">
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Form Kaydını Sil</h3>
                    <p className="text-gray-600 dark:text-gray-400">Bu işlem geri alınamaz</p>
                  </div>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-6">
                  <strong>{selectedSubmission.first_name} {selectedSubmission.last_name}</strong> adlı kişinin form kaydını kalıcı olarak silmek istediğinize emin misiniz?
                </p>
                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setSelectedSubmission(null);
                    }}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={() => deleteSubmission(selectedSubmission.id)}
                    className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Sil</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default WebsiteData;
